import {ColumnFilterItem} from "antd/lib/table";
import locales from "../locales";
import * as React from 'react';
import {Invoice} from "../api/dtos/dtos";
import {Link} from "react-router-dom";
import * as paths from "../paths";

export const enum InvoiceSource {
  RENTAL = 'RENTAL',
  SALE = 'SALE',
  MAINTENANCE = 'MAINTENANCE',
  MANUAL = 'MANUAL',
}


export const invoiceSourceToText = {
  [InvoiceSource.RENTAL]: locales.rental,
  [InvoiceSource.SALE]: locales.sale,
  [InvoiceSource.MAINTENANCE]: locales.maintenance,
  [InvoiceSource.MANUAL]: locales.manual,
};

export function getInvoiceStateFilters(): ColumnFilterItem[] {
  return [InvoiceState.CREATED, InvoiceState.SENT, InvoiceState.DELETED, InvoiceState.CANCELLED]
    .map(state => ({text: invoiceStateToText[state], value: state}));
}

export function getInvoiceSourceFilters(): ColumnFilterItem[] {
  return [InvoiceSource.RENTAL, InvoiceSource.SALE, InvoiceSource.MAINTENANCE, InvoiceSource.MANUAL]
    .map(source => ({text: invoiceSourceToText[source], value: source}));
}

export const enum InvoiceState {
  CREATED = 'CREATED',
  SENT = 'SENT',
  DELETED = 'DELETED',
  CANCELLED = 'CANCELLED',
}

export const invoiceStateToText = {
  [InvoiceState.CREATED]: locales.invoice_state_created,
  [InvoiceState.SENT]: locales.invoice_state_sent,
  [InvoiceState.DELETED]: locales.invoice_state_deleted,
  [InvoiceState.CANCELLED]: locales.invoice_state_cancelled,
};

const stateToColorClass = {
  [InvoiceState.CREATED]: 'yellow',
  [InvoiceState.SENT]: 'green',
  [InvoiceState.DELETED]: 'gray',
  [InvoiceState.CANCELLED]: 'red',
};

export function renderInvoiceState(state: string | undefined): React.ReactNode {
  const txt = state ? invoiceStateToText[state] : locales.invoice_state_created;
  return (
    <div style={{whiteSpace: 'nowrap'}}>
      <span style={{marginRight: 8}}>{txt}</span>
      {renderStateSpan(state)}
    </div>
  );
}

export function renderInvoiceStateCondensed(state: string | undefined): React.ReactNode {
  const txt = state ? invoiceStateToText[state] : locales.invoice_state_created;
  return (
    <div style={{textAlign: 'center'}} title={txt}>
      {renderStateSpan(state)}
    </div>
  );
}

export function renderStateSpan(state: string | undefined): React.ReactNode {
  const colorCls = state ? stateToColorClass[state] : 'grey';
  return <span className={'state-dot ' + colorCls}/>;
}

export const enum InvoiceType {
  PROFORMA = 'PROFORMA',
  PREPAYMENT = 'PREPAYMENT',
  INVOICE  = 'INVOICE',
  FINAL = 'FINAL',
  STORNO = 'STORNO',
  CORRECTIVE = 'CORRECTIVE',
}

export const invoiceTypeToText = {
  [InvoiceType.PROFORMA]: locales.invoice_type_proforma,
  [InvoiceType.PREPAYMENT]: locales.invoice_type_prepayment,
  [InvoiceType.INVOICE]: locales.invoice_type_invoice,
  [InvoiceType.FINAL]: locales.invoice_type_final,
  [InvoiceType.STORNO]: locales.invoice_type_storno,
  [InvoiceType.CORRECTIVE]: locales.invoice_type_corrective,
};

const typeToColorClass = {
  [InvoiceType.PROFORMA]: 'purple',
  [InvoiceType.PREPAYMENT]: 'blue',
  [InvoiceType.INVOICE]: 'gray',
  [InvoiceType.FINAL]: 'orange',
  [InvoiceType.STORNO]: 'red',
  [InvoiceType.CORRECTIVE]: 'gold',
};

export function renderInvoiceType(state: string | undefined): React.ReactNode {
  const txt = state ? invoiceTypeToText[state] : locales.invoice_type_invoice;
  return (
    <div style={{whiteSpace: 'nowrap'}}>
      <span style={{marginRight: 8}}>{txt}</span>
      {renderInvoiceTypeSpan(state)}
    </div>
  );
}

export function renderInvoiceTypeCondensed(type: string | undefined): React.ReactNode {
  const txt = type ? invoiceTypeToText[type] : locales.invoice_type_invoice;
  return (
    <div style={{textAlign: 'center'}} title={txt}>
      {renderInvoiceTypeSpan(type)}
    </div>
  );
}

export function renderInvoiceTypeSpan(state: string | undefined): React.ReactNode {
  const colorCls = state ? typeToColorClass[state] : 'grey';
  return <span className={'state-dot ' + colorCls}/>;
}

export function renderInvoiceSource(invoice: Invoice): React.ReactNode {
  if (!invoice.source) {
    return '-';
  }

  const sourceText = invoiceSourceToText[invoice.source];
  const link = invoice.sourceLink;

  if (link) {
    switch (invoice.source) {
      case InvoiceSource.SALE:
        return <>{sourceText} {link.id && <Link
            to={paths.viewSalePath(link.id)}>{` (${link.displayNumber})`}</Link>}</>;
      case InvoiceSource.MAINTENANCE:
        return <>{sourceText} {link.id && <Link
            to={paths.viewMaintenanceHistoryItemPath(link.id)}>{` (${link.displayNumber})`}</Link>}</>;
      case InvoiceSource.RENTAL:
        if (link.year && link.month) {
          return <Link
            to={paths.clientSummaryPath(invoice.client.id!!, link.year, link.month - 1)}>{sourceText}</Link>;
        }
        return sourceText;
      default:
        return sourceText;
    }
  }

  return sourceText;
}




// WEBPACK FOOTER //
// ./src/invoicing/invoice.tsx